import { DOCUMENT } from '@pkg/config/sanity/schemas';
import type { Ref } from 'vue';
import { useSanityData } from '@pkg/sanity-utils/composables/useSanityData';
import { listingByFilter } from '~/apis/sanity/queries/listingByFilter';
import { FULL_LISTING_POSTS_NUMBER } from '~/app/types/listingTypes';
import type { ListingData, ListingParams } from '~/app/types/listingTypes';
import { useListingFilterState } from '~/app/composables/useListingFilters';

/**
 * Listing posts fetch. Must register `useSanityData` before any `await` so SPA
 * navigations still fire `/api/sanity-secure` (Nuxt useFetch-after-await pitfall).
 */
export async function useListingContent(blockInstance: Ref<HTMLDivElement | null>, optionalParams: ListingParams) {
  const { locale } = useI18n();
  const route = useRoute();
  const listingQuery = listingByFilter();
  const postsPerPage = optionalParams.postsPerPage || FULL_LISTING_POSTS_NUMBER;
  const taxonomy = typeof optionalParams.taxonomy === 'string' ? optionalParams.taxonomy : '';
  const activeFilter = typeof optionalParams.activeFilter === 'object' ? optionalParams.activeFilter : null;

  // Sync only — do not await taxonomy here (BaseFilter loads that when shown).
  const { listingKey, effectiveActiveFilter, updateFilter } = useListingFilterState({
    sanityKey: optionalParams.sanityKey,
    listingType: optionalParams.type,
    taxonomy,
    postsPerPage,
    initialActiveFilter: activeFilter,
  });

  const urlPage = computed(() => {
    const pageParam = route.query[`page_${listingKey}`];
    if (pageParam && typeof pageParam === 'string') {
      const pageNum = parseInt(pageParam, 10);
      return isNaN(pageNum) || pageNum < 1 ? 1 : pageNum;
    }
    return 1;
  });

  const params = reactive<ListingParams>({
    type: optionalParams.type || DOCUMENT.ABOUT_FOOD_WASTE_BLOG,
    locale: unref(locale),
    page: urlPage.value,
    sliceStart: 0,
    sliceEnd: postsPerPage,
    activeFilter: effectiveActiveFilter.value ? [...effectiveActiveFilter.value] : null,
    taxonomy,
    sanityKey: listingKey,
  });

  watch(urlPage, (newPage) => {
    params.page = newPage;
    if (newPage === 1) {
      resetSlice();
    } else {
      handleSlice(newPage);
    }
  }, { immediate: true });

  watch(effectiveActiveFilter, (newFilter) => {
    params.activeFilter = newFilter ? [...newFilter] : null;
  }, { immediate: true });

  // First await in this composable — useFetch registers before setup yields.
  const { data } = await useSanityData<ListingData>(listingQuery, params);

  function handleSlice(selectedPage: number) {
    if (selectedPage > 1) {
      const sliceEnd = selectedPage * postsPerPage;
      params.sliceStart = sliceEnd - postsPerPage;
      params.sliceEnd = sliceEnd;
    }
  }

  function resetSlice() {
    params.sliceStart = 0;
    params.sliceEnd = postsPerPage;
    params.page = 1;
  }

  function handleFilter(filterSlug: Array<string>) {
    updateFilter(filterSlug);
    resetSlice();
  }

  return {
    data,
    handleFilter,
    params,
    listingKey,
  };
}
